import React, { useState, useEffect, useRef } from 'react';
import { Search, Download, Edit2, Save, X, MapPin, Building2, Home, FileText, Briefcase, Send, Eye, Loader2, Filter, ChevronDown, ChevronUp, Globe } from "lucide-react";
import apiService from '../config';
import EditLandholdingList from './EditLandholdinglist';
import { generateAnushuchiOnePDF } from './Anushuchi1';
import NamunaK from './Namuna_k'; // Import NamunaK component
import { useLanguage } from '../StoreContext/LanguageContext';
import { useSignature } from '../DigitalSignature/UseSignatue';
import SignaturePad from '../DigitalSignature/SignaturePad';
import CoveringLetters from './CoveringLetters';

export default function LandRecordsSystem({ setSelectedComponent, setSubComponent, modalParams, shouldRestoreModal, onNavigateWithModal, setShouldRestoreModal }) {

  // =============================================================================
  // STATE MANAGEMENT - Organized by purpose
  // =============================================================================

  // Location states
  const [districts, setDistricts] = useState([]);
  const [talukas, setTalukas] = useState([]);
  const [villages, setVillages] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedTaluka, setSelectedTaluka] = useState('');
  const [selectedVillage, setSelectedVillage] = useState('');
  const { language, translations } = useLanguage();


  // Filter states
  const [filterMode, setFilterMode] = useState('all');
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [showPDFModal, setShowPDFModal] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState('');


  // Loading states
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingTalukas, setLoadingTalukas] = useState(false);
  const [loadingVillages, setLoadingVillages] = useState(false);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [loading, setLoading] = useState(true);

  // Data states
  const [landRecords, setLandRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);

  // Project states
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [currentProposalId, setCurrentProposalId] = useState(null);

  // Modal states
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showEditLandholdingModal, setShowEditLandholdingModal] = useState(false);
  const [showSendToCollectorModal, setShowSendToCollectorModal] = useState(false);
  const [editingLandholdingId, setEditingLandholdingId] = useState(null);

  // Add these state variables to your existing state management section
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showProgress, setShowProgress] = useState(false);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState({
    title: '',
    message: '',
    onConfirm: null,
    onCancel: null,
    type: 'warning'
  });
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    title: '',
    message: '',
    type: 'success'
  });

  // User and role states
  const [users, setUsers] = useState([]);
  const [laoUsers, setLaoUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedLao, setSelectedLao] = useState('');
  const [assignLAO, setAssignLAO] = useState('');
  const [sendToRole, setSendToRole] = useState('LAO');
  const [userRole, setUserRole] = useState(null);

  const [sendingToSamanvay, setSendingToSamanvay] = useState(false);
  const [sendingToCollector, setSendingToCollector] = useState(false);
  const [sendingToLAO, setSendingToLAO] = useState(false);
 
  // Form and UI states
  const [remarks, setRemarks] = useState('Approved');
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [sendingToCollecotr, setSendingToCollecotr] = useState(false);
  const [changeLao, setChangeLao] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [documentUrl, setDocumentUrl] = useState('');
  const [showNamunaKModal, setShowNamunaKModal] = useState(false); // State to manage NamunaK modal visibility

  // =============================================================================
  // CONSTANTS
  // =============================================================================

  const ROLES = {
    PROJECT_INCHARGE: 18,
    SAMANVAY: 6,
    COLLECTOR: 1,
    LAO: 3
  };

  // =============================================================================
  // HELPER FUNCTIONS
  // =============================================================================

  // Check if user has valid location selection (at least district)
  const hasValidLocationSelection = selectedDistrict;

  // Check if data can be shown based on filter mode
  const canShowData = filterMode === 'all' || (filterMode === 'location' && hasValidLocationSelection);

  // Digital signature
  const { uploadStatus, uploadError, selectedSignDocument, resetSignatureState, signDocument } = useSignature();
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [pdfForSigning, setPdfForSigning] = useState(null);

  // Get current location description for display
  const getCurrentLocationDescription = () => {
    if (filterMode === 'all') {
      return 'All Locations';
    }

    if (selectedDistrict && selectedTaluka && selectedVillage) {
      return `Village: ${selectedVillage}, ${selectedTaluka}, ${selectedDistrict}`;
    } else if (selectedDistrict && selectedTaluka) {
      return `All Villages in ${selectedTaluka}, ${selectedDistrict}`;
    } else if (selectedDistrict) {
      return `All Records in ${selectedDistrict} District`;
    } else {
      return 'No location selected';
    }
  };

  // Get location names for display
  const getLocationNames = () => {
    const districtObj = districts.find(d =>
      (d.id || d.dist_no)?.toString() === selectedDistrict?.toString()
    );
    const district = districtObj?.name || districtObj?.eng_dist_name || '';

    const talukaObj = talukas.find(t =>
      (t.talukaNo || t.tehsil_no || t.id)?.toString() === selectedTaluka?.toString()
    );
    const taluka = talukaObj?.eng_tehsil_name || talukaObj?.tehsil_name || '';

    const villageObj = villages.find(v => {
      const villageId = v.VILL_ID || v.id || v.vill_id || v.village_id;
      return villageId?.toString() === selectedVillage?.toString();
    });
    const village = villageObj?.eng_village_name ||
      villageObj?.Village_Name ||
      villageObj?.village_name ||
      villageObj?.name || '';

    return { district, taluka, village };
  };

  // Reset modal state
  const resetModalState = () => {
    setSelectedUser('');
    setRemarks('Approved');
    setSelectedDocument(null);
    setUsers([]);
  };

  // Helper functions to show enhanced modals
  const showAlert = (title, message, type = 'info') => {
    setAlertConfig({ title, message, type });
    setShowAlertModal(true);
  };

  const showConfirm = (title, message, onConfirm, onCancel = null, type = 'warning') => {
    setConfirmConfig({ title, message, onConfirm, onCancel, type });
    setShowConfirmModal(true);
  };

  // =============================================================================
  // DATA LOADING FUNCTIONS
  // =============================================================================

  // Load districts  + selectedProposal
  const loadDistricts = async () => {
    setLoadingDistricts(true);
    try {
      const response = await apiService.getDistricts();
      const districtsData = Array.isArray(response.data) ? response.data : [];
      setDistricts(districtsData);

      // If selectedProposal has a district, auto-select it (only if not already selected)
      const proposalStr = localStorage.getItem('selectedProposal');
      if (proposalStr) {
        try {
          const proposal = JSON.parse(proposalStr);
          const desired = proposal.dist_name || proposal.distName;
          if (desired && !selectedDistrict) {
            // try to pick the matching district entry (prefer exact name match)
            const found = districtsData.find(d =>
              d.dist_name === desired || d.name === desired || d.eng_dist_name === desired
            );
            setSelectedDistrict(found ? (found.dist_name || found.id) : desired);
          }
        } catch (e) {
          console.error('Error parsing selectedProposal in loadDistricts:', e);
        }
      }
    } catch (error) {
      console.error("Failed to fetch districts:", error);
      setDistricts([]);
    } finally {
      setLoadingDistricts(false);
    }
  };

  // Load talukas + selectedProposal
  const loadTalukas = async () => {
    if (!selectedDistrict) return;

    setLoadingTalukas(true);
    try {
      const response = await apiService.getTalukas(selectedDistrict);
      const talukasData = Array.isArray(response.data) ? response.data : [];
      setTalukas(talukasData);

      // Auto-select taluka if selectedProposal has it and current selection is empty
      const proposalStr = localStorage.getItem('selectedProposal');
      if (proposalStr) {
        try {
          const proposal = JSON.parse(proposalStr);
          const desiredTaluka = proposal.taluka_name || proposal.talukaName;
          if (desiredTaluka && !selectedTaluka) {
            const found = talukasData.find(t =>
              t.taluka_name === desiredTaluka || t.tehsil_name === desiredTaluka || t.eng_tehsil_name === desiredTaluka
            );
            setSelectedTaluka(found ? (found.taluka_name || found.id) : desiredTaluka);
          }
        } catch (e) {
          console.error('Error parsing selectedProposal in loadTalukas:', e);
        }
      } else {
        // no proposal default — clear dependent selections (this is original behaviour)
        setSelectedTaluka('');
        setVillages([]);
        setSelectedVillage('');
      }
    } catch (error) {
      console.error("Failed to fetch talukas:", error);
      setTalukas([]);
    } finally {
      setLoadingTalukas(false);
    }
  };

  // Load villages + slectedProposal
  const loadVillages = async () => {
    if (!selectedDistrict || !selectedTaluka) return;

    setLoadingVillages(true);
    try {
      const response = await apiService.getVillages(selectedDistrict, selectedTaluka);
      const villagesData = Array.isArray(response.data) ? response.data : [];
      setVillages(villagesData);

      // Auto-select village if selectedProposal has it and current selection is empty
      const proposalStr = localStorage.getItem('selectedProposal');
      if (proposalStr) {
        try {
          const proposal = JSON.parse(proposalStr);
          const desiredVillage = proposal.village_name || proposal.villageName;
          if (desiredVillage && !selectedVillage) {
            const found = villagesData.find(v =>
              v.village_name === desiredVillage ||
              v.Village_Name === desiredVillage ||
              v.eng_village_name === desiredVillage ||
              v.name === desiredVillage
            );
            setSelectedVillage(found ? (found.village_name || found.id || found.village_id) : desiredVillage);
          }
        } catch (e) {
          console.error('Error parsing selectedProposal in loadVillages:', e);
        }
      } else {
        // no proposal default — clear village selection (original behaviour)
        setSelectedVillage('');
      }
    } catch (error) {
      console.error("Failed to fetch villages:", error);
      setVillages([]);
    } finally {
      setLoadingVillages(false);
    }
  };

  // Load land records for all locations
  const loadLandRecordsForAll = async () => {
    if (!currentProjectId || !currentProposalId) return;

    setLoadingRecords(true);
    try {
      const locationParams = { proposalId: currentProposalId };
      const result = await apiService.getLandHolding(currentProjectId, locationParams);

      if (result.status === 200 && Array.isArray(result.data)) {
        const transformedRecords = result.data.map((item, index) => ({
          id: item.LandAcquisition_id || index + 1,
          seqNo: index + 1,
          gatNo: item.survey_no || 'N/A',
          area: item.aqr_area || item.total_area || 0,
          ...item
        }));
        setLandRecords(transformedRecords);
        setFilteredRecords(transformedRecords);
      } else {
        setLandRecords([]);
        setFilteredRecords([]);
      }
    } catch (error) {
      console.error('Error fetching all land holdings:', error);
      setLandRecords([]);
      setFilteredRecords([]);
    } finally {
      setLoadingRecords(false);
    }
  };

  // Load land records for specific location
  const loadLandRecordsForLocation = async () => {
    if (!currentProjectId || !currentProposalId) return;

    setLoadingRecords(true);
    try {
      const locationParams = { proposalId: currentProposalId };
      if (selectedDistrict) locationParams.distNo = selectedDistrict;
      if (selectedTaluka) locationParams.talukaNo = selectedTaluka;
      if (selectedVillage) locationParams.villageNo = selectedVillage;

      const result = await apiService.getLandHolding(currentProjectId, locationParams);

      if (result.status === 200 && Array.isArray(result.data)) {
        const transformedRecords = result.data.map((item, index) => ({
          id: item.LandAcquisition_id || index + 1,
          seqNo: index + 1,
          gatNo: item.survey_no || 'N/A',
          area: item.aqr_area || item.total_area || 0,
          ...item
        }));
        setLandRecords(transformedRecords);
        setFilteredRecords(transformedRecords);
      } else {
        setLandRecords([]);
        setFilteredRecords([]);
      }
    } catch (error) {
      console.error('Error fetching land holdings:', error);
      setLandRecords([]);
      setFilteredRecords([]);
    } finally {
      setLoadingRecords(false);
    }
  };

  // Refresh records based on current filter mode
  const refreshRecords = async () => {
    if (filterMode === 'all') {
      await loadLandRecordsForAll();
    } else if (selectedDistrict && selectedTaluka && selectedVillage) {
      await loadLandRecordsForLocation();
    }
  };

  // =============================================================================
  // USER AND API FUNCTIONS
  // =============================================================================

  // Fetch users by role
  const fetchUsersCollector = async () => {
    setLoadingUsers(true);
    try {
      const response = await apiService.getAllUserCollector();
      if (response.status === 200 && response.data) {
        const data = Array.isArray(response.data) ? response.data : [];
        setUsers(data);
        if (data.length > 0) {
          setSelectedUser(data[0].id || data[0].usr_id);
        }
      }
    } catch (error) {
      console.error('Error fetching Collector users:', error);
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchUsersLAO = async () => {
    setLoadingUsers(true);
    try {
      const response = await apiService.getAllUserRoleLAO();
      if (response.status === 200 && response.data) {
        const users = Array.isArray(response.data) ? response.data : [];
        setLaoUsers(users);
      }
    } catch (error) {
      console.error('Error fetching LAO users:', error);
      setLaoUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchUsersSamanvay = async () => {
    setLoadingUsers(true);
    try {
      const response = await apiService.getAllUserRoleSamanvay();
      if (response.status === 200 && response.data) {
        setUsers(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error('Error fetching Samanvay users:', error);
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchAssignLAO = async () => {
    setLoadingUsers(true);
    const role = 3;
    const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || '{}');
    const projectId = selectedProject?.id || selectedProject?.project_id;

    try {
      const response = await apiService.getAssignLAO(role, projectId, currentProposalId);
      if (response.status === 200 && response.data) {
        setAssignLAO(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error('Error fetching assigned LAO:', error);
      setAssignLAO([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Button status check
  const fetchButtonStatus = async () => {
    try {
      if (!currentProjectId || !currentProposalId) return;

      setLoading(true);
      let response;

      switch (userRole) {
        case ROLES.PROJECT_INCHARGE:
          response = await apiService.getSamanvayButton(currentProjectId, currentProposalId);
          break;
        case ROLES.SAMANVAY:
          response = await apiService.getCollectorButton(currentProjectId, currentProposalId);
          break;
        case ROLES.COLLECTOR:
          response = await apiService.getLAOButton(currentProjectId, currentProposalId);
          break;
        default:
          return;
      }
      setButtonDisabled(!response.buttonDisable);
    } catch (error) {
      console.error("Error fetching button state:", error);
    } finally {
      setLoading(false);
    }
  };

  // =============================================================================
  // EVENT HANDLERS
  // =============================================================================

  // Handle location changes
  const handleDistrictChange = (e) => {
    const selectedDistrictId = e.target.value;
    setSelectedDistrict(selectedDistrictId);
    setSelectedTaluka('');
    setSelectedVillage('');
    setTalukas([]);
    setVillages([]);
    setLandRecords([]);
    setFilteredRecords([]);
  };

  const handleTalukaChange = (e) => {
    const selectedTalukaId = e.target.value;
    setSelectedTaluka(selectedTalukaId);
    setSelectedVillage('');
    setVillages([]);
    setLandRecords([]);
    setFilteredRecords([]);
  };

  const handleVillageChange = (e) => {
    const selectedVillageId = e.target.value;
    setSelectedVillage(selectedVillageId);
    setLandRecords([]);
    setFilteredRecords([]);
  };

  // Handle search functionality
  const handleSearch = (searchTerm) => {
    if (!searchTerm) {
      setFilteredRecords(landRecords);
      return;
    }

    const filtered = landRecords.filter(record =>
      record.gatNo.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.seqNo.toString().includes(searchTerm) ||
      record.landholder_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.survey_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.LandAcquisition_id?.toString().includes(searchTerm)
    );
    setFilteredRecords(filtered);
  };

  // Handle edit landholding
  const handleEditLandholding = (record) => {
    setEditingLandholdingId(record.LandAcquisition_id);
    setShowEditLandholdingModal(true);
    localStorage.setItem('editingLandholding', JSON.stringify(record));
  };

  // Handle direct payment click
  const handleDirectPaymentClick = () => {
    localStorage.setItem('currentProjectId', currentProjectId);
    localStorage.setItem('currentProposalId', currentProposalId);

    if (setSelectedComponent) {
      setSelectedComponent('DirectPayment');
    } else {
      localStorage.setItem('goToSubComponent', 'DirectPayment');
      window.location.reload();
    }
  };

  const handleOpenNamunaK = () => {
    setShowNamunaKModal(true); // Open the NamunaK modal
  };

  const handleCloseNamunaK = () => {
    setShowNamunaKModal(false); // Close the NamunaK modal
  };

  // Handle sending to different roles
  const handleSendToSamanvayClick = () => {
    resetModalState();
    setSendToRole('Samanvay');
    if (buttonDisabled) {
      showConfirm(
        'Confirm Re-submission',
        'You have already sent this proposal once. Are you sure you want to send it again?',
        () => {
          localStorage.setItem('sentToSamanvay', 'true');
          localStorage.setItem('sentProposalId', currentProposalId);
          setShowSendToCollectorModal(true);
          fetchUsersSamanvay();
        }
      );
      return;
    }

    localStorage.setItem('sentToSamanvay', 'true');
    localStorage.setItem('sentProposalId', currentProposalId);
    setShowSendToCollectorModal(true);
    fetchUsersSamanvay();
  };

  const handleSendToCollectorClick = () => {
    resetModalState();
    setSendToRole('Collector');
    if (buttonDisabled) {
      showConfirm(
        'Confirm Re-submission',
        'You have already sent this proposal once. Are you sure you want to send it again?',
        () => {
          setShowSendToCollectorModal(true);
          fetchUsersCollector();
        }
      );
      return;
    }
    setShowSendToCollectorModal(true);
    fetchUsersCollector();
  };

  const handleSendToLAOClick = async () => {
    resetModalState();
    setSendToRole('LAO');
    if (buttonDisabled) {
      showConfirm(
        'Confirm Re-submission',
        'You have already sent this proposal once. Are you sure you want to send it again?',
        () => {
          setShowSendToCollectorModal(true);
          fetchAssignLAO();
          fetchUsersLAO();
        }
      );
      return;
    }
    setShowSendToCollectorModal(true);
    await fetchAssignLAO();
    await fetchUsersLAO();

    // ✅ mark as disabled after first success
    setButtonDisabled(true);
  };

  // Handle document view
  const handleViewDocument = (file) => {
    const url = URL.createObjectURL(file);
    setDocumentUrl(url);
    setShowModal(true);
  };

  // Close modal
  const closeModal = () => {
    setShowSendToCollectorModal(false);
    resetModalState();
  };

  // =============================================================================
  // API SUBMISSION FUNCTIONS
  // =============================================================================

  const handleAssignLAO = async (laoUserId) => {
    const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || '{}');
    const projectId = selectedProject?.id || selectedProject?.project_id;
    const proposalId = currentProposalId;

    if (!laoUserId || !projectId || !proposalId) {
      showAlert('Missing Information', 'All fields are required to assign LAO Officer.'); return;
    }

    const payload = {
      usr_id: laoUserId,
      usr_role: 3,
      project_id: projectId,
      acquisition_proposal_id: proposalId,
      is_active: 1
    };

    const response = await apiService.addAssignRole(payload);
    console.log("✅ LAO Assigned Successfully:", response);
  };

  // const handleSendToCollector = async (collectorUserId) => {
  //   const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || '{}');
  //   const projectId = selectedProject?.id || selectedProject?.project_id;
  //   const proposalId = currentProposalId;

  //   setSendingToCollector(true);
  //   try {
  //     const formData = new FormData();
  //     formData.append('project_id', projectId);
  //     formData.append('proposal_id', proposalId);
  //     formData.append('remarks', remarks);
  //     formData.append('usr_id', collectorUserId);
  //     formData.append('document', selectedDocument);

  //     const response = await apiService.postTrackingCollector(formData);

  //     if (response.status === 200) {
  //       setSuccessMessage('Successfully sent to Collector!');
  //       setShowSuccess(true);
  //       setTimeout(() => setShowSuccess(false), 5000);
  //     }
  //   } catch (error) {
  //     showAlert('Failed to send to Collector. Please check your connection and try again.');
  //   } finally {
  //     setSendingToCollector(false);
  //   }
  // };

  // const handleSendToLAO = async () => {
  //   const finalSelectedUser = selectedLao ||
  //     (assignLAO && Array.isArray(assignLAO) && assignLAO.length > 0
  //       ? assignLAO[0].usr_id || assignLAO[0].id
  //       : null);

  //   if (!finalSelectedUser || !selectedDocument) {
  //     alert('Please select a LAO and upload a document');
  //     return;
  //   }

  //   const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || '{}');
  //   const projectId = selectedProject?.id || selectedProject?.project_id;
  //   const proposalId = currentProposalId;

  //   setSendingToLAO(true); // CHANGE FROM setSendingToCollecotr to setSendingToLAO
  //   try {
  //     const formData = new FormData();
  //     formData.append('project_id', projectId);
  //     formData.append('proposal_id', proposalId);
  //     formData.append('remarks', remarks);
  //     formData.append('usr_id', finalSelectedUser);
  //     formData.append('document', selectedDocument);

  //     const response = await apiService.postTrackingLAO(formData);

  //     if (response.status === 200) {
  //       setSuccessMessage('Successfully Send to LAO Officer!');
  //       setShowSuccess(true);
  //       setTimeout(() => setShowSuccess(false), 3000);

  //       setShowSendToCollectorModal(false);
  //       setSelectedUser('');
  //       setSelectedLao('');
  //       setChangeLao(false);
  //       setRemarks('Approved');
  //       setSelectedDocument(null);
  //       fetchButtonStatus();
  //     }
  //   } catch (error) {
  //     console.error('Error appointing LAO:', error);
  //     showAlert('Failed to appoint LAO Officer.');
  //   } finally {
  //     setSendingToLAO(false); // CHANGE FROM setSendingToCollecotr to setSendingToLAO
  //   }
  // };

  // const handleSendToSamanvay = async () => {
  //   if (!selectedUser || !selectedDocument) {
  //     alert('Please select a user and upload a document');
  //     return;
  //   }

  //   const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || '{}');
  //   const projectId = selectedProject?.id || selectedProject?.project_id;
  //   const proposalId = currentProposalId;

  //   setSendingToSamanvay(true);
  //   try {
  //     const formData = new FormData();
  //     formData.append('project_id', projectId);
  //     formData.append('proposal_id', proposalId);
  //     formData.append('remarks', remarks);
  //     formData.append('usr_id', selectedUser);
  //     formData.append('document', selectedDocument);

  //     const response = await apiService.postTrackingSamanvay(formData);

  //     if (response.status === 200) {
  //       setSuccessMessage('Successfully sent to Samanvay Officer!');
  //       setShowSuccess(true);
  //       setTimeout(() => setShowSuccess(false), 5000);

  //       setShowSendToCollectorModal(false);
  //       setSelectedUser('');
  //       setRemarks('Approved');
  //       setSelectedDocument(null);
  //       fetchButtonStatus();
  //     }
  //   } catch (error) {
  //     console.error('Error sending to Samanvay:', error);
  //     showAlert('Failed to send to Samanvay Officer. Please check your connection and try again.');
  //   } finally {
  //     setSendingToSamanvay(false);
  //   }
  // };

  // Modified API submission functions with progress tracking
  const handleSendToCollector = async (collectorUserId) => {
    const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || '{}');
    const projectId = selectedProject?.id || selectedProject?.project_id;
    const proposalId = currentProposalId;

    setSendingToCollector(true);
    setShowProgress(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('project_id', projectId);
      formData.append('proposal_id', proposalId);
      formData.append('remarks', remarks);
      formData.append('usr_id', collectorUserId);
      formData.append('document', selectedDocument);

      // Progress steps before API call
      setUploadProgress(10); // Initial preparation
      await new Promise(resolve => setTimeout(resolve, 300));

      setUploadProgress(30); // Document preparation
      await new Promise(resolve => setTimeout(resolve, 400));

      setUploadProgress(60); // Ready to send
      await new Promise(resolve => setTimeout(resolve, 300));

      const response = await apiService.postTrackingCollector(formData);

      if (response.status === 200) {
        setUploadProgress(100); // Only set to 100% on successful API response
        await new Promise(resolve => setTimeout(resolve, 500)); // Show 100% briefly

        setSuccessMessage('Successfully sent to Collector!');
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 5000);
      } else {
        setUploadProgress(0); // Reset on failure
      }
    } catch (error) {
      setUploadProgress(0); // Reset on error
      showAlert('Failed to send to Collector. Please check your connection and try again.');
    } finally {
      setSendingToCollector(false);
      setShowProgress(false);
      setUploadProgress(0);
    }
  };

  const handleSendToLAO = async () => {
    const finalSelectedUser = selectedLao ||
      (assignLAO && Array.isArray(assignLAO) && assignLAO.length > 0
        ? assignLAO[0].usr_id || assignLAO[0].id
        : null);

    if (!finalSelectedUser || !selectedDocument) {
      alert('Please select a LAO and upload a document');
      return;
    }

    const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || '{}');
    const projectId = selectedProject?.id || selectedProject?.project_id;
    const proposalId = currentProposalId;

    setSendingToLAO(true);
    setShowProgress(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('project_id', projectId);
      formData.append('proposal_id', proposalId);
      formData.append('remarks', remarks);
      formData.append('usr_id', finalSelectedUser);
      formData.append('document', selectedDocument);

      // Progress steps before API call
      setUploadProgress(15); // Preparing assignment
      await new Promise(resolve => setTimeout(resolve, 400));

      setUploadProgress(40); // Uploading documents
      await new Promise(resolve => setTimeout(resolve, 500));

      setUploadProgress(70); // Processing request
      await new Promise(resolve => setTimeout(resolve, 300));

      const response = await apiService.postTrackingLAO(formData);

      if (response.status === 200) {
        setUploadProgress(100); // Only set to 100% on successful API response
        await new Promise(resolve => setTimeout(resolve, 500)); // Show 100% briefly

        setSuccessMessage('Successfully Send to LAO Officer!');
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);

        setShowSendToCollectorModal(false);
        setSelectedUser('');
        setSelectedLao('');
        setChangeLao(false);
        setRemarks('Approved');
        setSelectedDocument(null);
        fetchButtonStatus();
      } else {
        setUploadProgress(0); // Reset on failure
      }
    } catch (error) {
      setUploadProgress(0); // Reset on error
      console.error('Error appointing LAO:', error);
      showAlert('Failed to appoint LAO Officer.');
    } finally {
      setSendingToLAO(false);
      setShowProgress(false);
      setUploadProgress(0);
    }
  };

  const handleSendToSamanvay = async () => {
    if (!selectedUser || !selectedDocument) {
      alert('Please select a user and upload a document');
      return;
    }

    const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || '{}');
    const projectId = selectedProject?.id || selectedProject?.project_id;
    const proposalId = currentProposalId;

    setSendingToSamanvay(true);
    setShowProgress(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('project_id', projectId);
      formData.append('proposal_id', proposalId);
      formData.append('remarks', remarks);
      formData.append('usr_id', selectedUser);
      formData.append('document', selectedDocument);

      // Progress steps before API call
      setUploadProgress(20); // Validating data
      await new Promise(resolve => setTimeout(resolve, 500));

      setUploadProgress(50); // Preparing upload
      await new Promise(resolve => setTimeout(resolve, 400));

      setUploadProgress(75); // Processing request
      await new Promise(resolve => setTimeout(resolve, 300));

      const response = await apiService.postTrackingSamanvay(formData);

      if (response.status === 200) {
        setUploadProgress(100); // Only set to 100% on successful API response
        await new Promise(resolve => setTimeout(resolve, 500)); // Show 100% briefly

        setSuccessMessage('Successfully sent to Samanvay Officer!');
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 5000);

        setShowSendToCollectorModal(false);
        setSelectedUser('');
        setRemarks('Approved');
        setSelectedDocument(null);
        fetchButtonStatus();
      } else {
        setUploadProgress(0); // Reset on failure
      }
    } catch (error) {
      setUploadProgress(0); // Reset on error
      console.error('Error sending to Samanvay:', error);
      showAlert('Failed to send to Samanvay Officer. Please check your connection and try again.');
    } finally {
      setSendingToSamanvay(false);
      setShowProgress(false);
      setUploadProgress(0);
    }
  };

  const handleSendToSurveyor = async () => {
    const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || '{}');
    const projectId = selectedProject?.id || selectedProject?.project_id;
    const proposalId = currentProposalId;

    if (sendToRole === 'Samanvay') {
      if (!selectedUser || !selectedDocument) {
        showAlert('Please select a user and upload a document');
        return;
      }
      await handleSendToSamanvay();
    }
    else if (sendToRole === 'LAO') {
      if (!selectedLao || !selectedDocument) {
        showAlert('Please select a user and upload a document');
        return;
      }
      await handleSendToLAO();
    }
    else if (sendToRole === 'Collector') {
      if (!selectedUser || !selectedLao || !selectedDocument) {
        showAlert('Please select Collector, LAO, and upload a document');
        return;
      }

      try {
        setSendingToCollector(true); // ADD THIS LINE
        await handleAssignLAO(selectedLao);
        await handleSendToCollector(selectedUser);
        setShowSendToCollectorModal(false);
        setSelectedUser('');
        setSelectedLao('');
        setChangeLao(false);
        setRemarks('Approved');
        setSelectedDocument(null);
        fetchButtonStatus();
      } catch (err) {
        console.error("Error in Collector flow:", err);
        showAlert("Something went wrong while processing.");
      } finally {
        setSendingToCollector(false); // ADD THIS LINE
      }
    }
  };

  // =============================================================================
  // USEEFFECT HOOKS - Organized by purpose
  // =============================================================================

  // Initialize user role
  useEffect(() => {
    try {
      const userData = localStorage.getItem("userData");
      if (userData) {
        const user = JSON.parse(userData);
        setUserRole(user.user_role || user.user_role_name);
      }
    } catch (error) {
      console.error("Error parsing user data:", error);
    }
  }, []);

  // Initialize project and proposal IDs
  useEffect(() => {
    const getProjectInfo = () => {
      const currentProjectData = localStorage.getItem('selectedProject');
      if (currentProjectData) {
        try {
          const projectData = JSON.parse(currentProjectData);
          return { id: projectData.project_id || projectData.id };
        } catch (e) {
          console.error('Error parsing current project data:', e);
        }
      }
      return { id: null };
    };

    const getProposalInfo = () => {
      const currentProposal = JSON.parse(localStorage.getItem('userData') || '{}');
      if (currentProposal) {
        try {
          return {
            proposalId: currentProposal?.data?.default_proposal ||
              currentProposal?.data?.proposal_id ||
              currentProposal?.default_proposal ||
              currentProposal?.proposal_id ||
              null
          }
        } catch (e) {
          console.error('Error parsing current project data:', e);
        }
      }
    };

    const projectInfo = getProjectInfo();
    const proposalInfo = getProposalInfo();

    setCurrentProjectId(projectInfo.id);
    setCurrentProposalId(proposalInfo.proposalId);
  }, []);

  // fetching from selectedProposal (dist/taluka/village) ----------
  useEffect(() => {
    try {
      const selProposalStr = localStorage.getItem('selectedProposal');
      if (selProposalStr) {
        const proposal = JSON.parse(selProposalStr);
        const dist = proposal.dist_name || proposal.distName || '';
        const tal = proposal.taluka_name || proposal.talukaName || '';
        const vill = proposal.village_name || proposal.villageName || '';

        if (dist) setSelectedDistrict(dist);
        if (tal) setSelectedTaluka(tal);
        if (vill) setSelectedVillage(vill);

        // persist to selectedLocationInfo so other components/flows see same defaults
        localStorage.setItem('selectedLocationInfo', JSON.stringify({
          projectId: currentProjectId || null,
          district: dist || '',
          taluka: tal || '',
          village: vill || ''
        }));

        // show the filter UI and switch to location mode so fetch logic runs automatically
        setFilterMode('location');
        setShowFilterPanel(true);
        return;
      }

      // fallback: if you already had a previously saved selectedLocationInfo
      const saved = localStorage.getItem('selectedLocationInfo');
      if (saved) {
        const loc = JSON.parse(saved);
        if (loc.district) setSelectedDistrict(loc.district);
        if (loc.taluka) setSelectedTaluka(loc.taluka);
        if (loc.village) setSelectedVillage(loc.village);
        if (loc.district || loc.taluka || loc.village) {
          setFilterMode('location');
          setShowFilterPanel(true);
        }
      }
    } catch (err) {
      console.error('Error reading selectedProposal for defaults', err);
    }
  }, [currentProjectId]); // run after project id is known


  const handleGenerateAndPreviewPDF = () => {
    const selectedLocation = getLocationNames();
    const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || '{}');

    const formatDataForExport = () => {
      const rows = [];
      const itemsPerRow = 3;
      for (let i = 0; i < filteredRecords.length; i += itemsPerRow) {
        const row = {};
        const items = [filteredRecords[i], filteredRecords[i + 1], filteredRecords[i + 2]];
        for (let j = 0; j < 3; j++) {
          const item = items[j];
          row[`srNo${j ? j + 1 : ''}`] = item?.seqNo || '';
          row[`surveyNo${j ? j + 1 : ''}`] = item?.gatNo || '';
          row[`area${j ? j + 1 : ''}`] = item?.area || '';
          row[`landholder${j ? j + 1 : ''}`] = item?.landholder_name || '';
          row[`totalArea${j ? j + 1 : ''}`] = item?.total_area || '';
        }
        rows.push(row);
      }
      return rows;
    };

    const formattedData = formatDataForExport();

    const htmlContent = `
    <html><head>
    <style>
       @page {
          size: A4 portrait;   /* Orientation */
          margin-top: 0.19in;
          margin-bottom: 0.25in;
          margin-left: 0.81in;
          margin-right: 0.5in;
        }
        body {
          font-family: 'DVOT-Surekh', 'Mangal', 'Arial', sans-serif;
        }      .header { text-align: center; margin: 20px; }
      .header h1 { font-size: 20px; }
      .table-container { width: 100%; border-collapse: collapse; }
      .table-container th, .table-container td {
        border: 1px solid black; padding: 6px 4px; text-align: center;
      }
        .table-wrapper {
  padding-left: 40px;
  padding-right: 40px;
}
      .footer { text-align: right;padding-right: 40px; margin-top: 20px; font-size: 11px; }
    </style>
    </head><body>
      <div class="header">
        <h1>Schedule - One</h1>
        <p>Land Acquisition Project Name- ${selectedProject.project_name || ''}</p>
        <p>Land Being Acquired for Irrigation Project</p>
      </div>
      <div class="table-wrapper">
      <table class="table-container">
        <thead>
          <tr>
            <th>Sr. No.</th><th>Survey No.</th><th>Acquisition Area</th><th>Landholder</th>
            <th>Sr. No.</th><th>Survey No.</th><th>Acquisition Area</th><th>Landholder</th>
            <th>Sr. No.</th><th>Survey No.</th><th>Acquisition Area</th><th>Landholder</th>
          </tr>
        </thead>
        <tbody>
          ${formattedData.map(row => `
            <tr>
              <td>${row.srNo}</td><td>${row.surveyNo}</td><td>${row.area}</td><td>${row.landholder}</td>
              <td>${row.srNo2}</td><td>${row.surveyNo2}</td><td>${row.area2}</td><td>${row.landholder2}</td>
              <td>${row.srNo3}</td><td>${row.surveyNo3}</td><td>${row.area3}</td><td>${row.landholder3}</td>
            </tr>`).join('')}
        </tbody>
      </table>
      </div>
      <div class="footer">Date: ${new Date().toLocaleDateString('en-GB')}</div>
    </body></html>
  `;

    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    setPdfBlobUrl(url);
    setShowPDFModal(true);
  };


  // Load initial data
  useEffect(() => {
    loadDistricts();
  }, []);

  // Load talukas when district changes
  useEffect(() => {
    loadTalukas();
  }, [selectedDistrict]);

  // Load villages when taluka changes
  useEffect(() => {
    loadVillages();
  }, [selectedDistrict, selectedTaluka]);

  // Load records based on filter mode
  useEffect(() => {
    if (filterMode === 'all' && currentProjectId && currentProposalId) {
      loadLandRecordsForAll();
    }
  }, [filterMode, currentProjectId, currentProposalId]);

  useEffect(() => {
    if (filterMode === 'location' && selectedDistrict && selectedTaluka && selectedVillage && currentProjectId && currentProposalId) {
      loadLandRecordsForLocation();
    } else if (filterMode === 'location') {
      setLandRecords([]);
      setFilteredRecords([]);
    }
  }, [filterMode, selectedDistrict, selectedTaluka, selectedVillage, currentProjectId, currentProposalId]);

  // Handle project changes
  useEffect(() => {
    const handleProjectChange = () => {
      if (filterMode === 'all') {
        loadLandRecordsForAll();
      } else if (selectedDistrict && selectedTaluka && selectedVillage) {
        loadLandRecordsForLocation();
      }
    };

    window.addEventListener('storage', handleProjectChange);
    window.addEventListener('projectChanged', handleProjectChange);

    return () => {
      window.removeEventListener('storage', handleProjectChange);
      window.removeEventListener('projectChanged', handleProjectChange);
    };
  }, [filterMode, selectedDistrict, selectedTaluka, selectedVillage]);

  // Handle LAO assignment logic
  useEffect(() => {
    if (showSendToCollectorModal && sendToRole === 'Collector') {
      fetchUsersLAO();
      fetchAssignLAO();
    }
  }, [showSendToCollectorModal, sendToRole]);

  useEffect(() => {
    if (assignLAO && assignLAO.length > 0 && !changeLao) {
      const preselectedLAO = assignLAO[0].usr_id || assignLAO[0].id;
      setSelectedLao(preselectedLAO);
    }
  }, [assignLAO, changeLao]);

  // Fetch button status
  useEffect(() => {
    fetchButtonStatus();
  }, [currentProjectId, currentProposalId, userRole]);

  // Reset modal state when user role changes
  useEffect(() => {
    resetModalState();
  }, [userRole]);

  // Global function for refresh
  window.refreshLandRecords = () => {
    if (selectedDistrict && selectedTaluka && selectedVillage) {
      window.dispatchEvent(new CustomEvent('projectChanged'));
    }
  };

  // Restore modal state from parameters
  useEffect(() => {
    if (shouldRestoreModal && modalParams) {
      // Restore modal open state
      setShowSendToCollectorModal(true);
      fetchUsersSamanvay();

      // Restore previous selections
      // Save for later restoration when users arrive
      pendingRestore.current = {
        user: modalParams.selectedUser || '',
        lao: modalParams.selectedLao || '',
        remarks: modalParams.remarks || '',
      };

      setRemarks(modalParams.remarks || '');
      setSendToRole(modalParams.sendToRole || 'Collector');

      // Reset flag so modal doesn't restore again automatically
      setShouldRestoreModal(false);
    }
  }, [shouldRestoreModal, modalParams]);

  useEffect(() => {
    if (pendingRestore.current) {
      if (users.length > 0 || laoUsers.length > 0) {
        setSelectedUser(pendingRestore.current.user);
        setSelectedLao(pendingRestore.current.lao);
        setRemarks(pendingRestore.current.remarks);

        pendingRestore.current = null; // clear after applying
      }
    }
  }, [users, laoUsers]);

  const pendingRestore = useRef(null);

  // Navigate to covering letters with modal state
  const handleCoveringLetterClick = () => {
    const modalState = {
      selectedUser,
      userId: selectedLao,
      remarks,
      sendToRole,
      selectedDocumentName: selectedDocument?.name || null,
      timestamp: new Date().getTime()
    };

    // Close the modal before navigating
    setShowSendToCollectorModal(false);

    // Pass modalState to CoveringLetters
    onNavigateWithModal('CoveringLetters', modalState);
  };

  // Progress Bar Component (add this as a new component)
  const ProgressBar = ({ progress, isVisible }) => {
    if (!isVisible) return null;

    return (
      <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
        <div
          className="bg-blue-600 h-2 rounded-full transition-all duration-500 ease-out"
          style={{ width: `${progress}%` }}
        />
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>{translations[language].saving}</span>
          <span>{progress}%</span>
        </div>
      </div>
    );
  };

  // =============================================================================
  // RENDER COMPONENT
  // =============================================================================

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-7xl mx-auto">

        {/* Enhanced Success Message */}
        {/* Simple Success Message */}
        {showSuccess && (
          <div className="fixed top-4 right-4 z-50">
            <div className="bg-white rounded-lg shadow-lg border-l-4 border-green-500 p-4 max-w-sm">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-green-600 font-bold">✓</span>
                  </div>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-medium text-gray-900">{translations[language]?.success}</h3>
                  <p className="text-sm text-gray-600 mt-1">{successMessage}</p>
                </div>
                <button
                  onClick={() => setShowSuccess(false)}
                  className="ml-4 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Loading Message */}
        {loadingRecords && (
          <div className="flex justify-center items-center h-80">
            <div className="text-center">
              <div className="inline-flex relative">
                <div className="w-16 h-16 rounded-full border-4 border-slate-700 border-t-transparent animate-spin"></div>
                <div className="w-16 h-16 rounded-full border-4 border-purple-500 border-b-transparent border-r-transparent animate-spin absolute inset-0" style={{ animationDuration: '1.5s', animationDirection: 'reverse' }}></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <MapPin className="h-6 w-6 text-purple-400 animate-pulse" />
                </div>
              </div>
              <p className="text-gray-400 mt-4">{translations[language]?.LoadingLandRecords}</p>
            </div>
          </div>
        )}

        {/* Search and Actions Bar - Always show when not loading */}
        {!loadingRecords && canShowData && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">

              {/* Search Box */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder={translations[language]?.SearchPlaceholder}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">

                {/* Filter Button */}
                <button
                  onClick={() => setShowFilterPanel(!showFilterPanel)}
                  className="flex items-center space-x-2 px-4 py-2.5 bg-orange-600 text-white rounded-lg font-medium transition-all duration-200 shadow-sm hover:shadow-md hover:bg-orange-700"
                >
                  <Filter className="h-4 w-4" />
                  <span>{translations[language]?.Filters}</span>
                  {showFilterPanel ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>

                {/* Direct Purchase Button */}
                {/* <button
                  onClick={handleDirectPaymentClick}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                >
                  <FileText className="mr-2 h-4 w-4" />
                  {translations[language]?.DirectPurchase}
                </button> */}

                {/* Download Button */}
                <button
                  // onClick={() => setShowDownloadModal(true)}
                  onClick={handleGenerateAndPreviewPDF}
                  disabled={filteredRecords.length === 0}
                  className="flex items-center px-6 py-3 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 shadow-lg transition-all duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  <Download className="mr-2 h-5 w-5" />
                  {translations[language]?.landRecordsData}
                </button>

                {/* Role-based Action Buttons */}
                <div className="flex gap-4">

                  {/* Project Incharge Button */}
                  {userRole === ROLES.PROJECT_INCHARGE && (
                    <>
                      {/* <button
                        onClick={handleOpenNamunaK}
                        className={`flex items-center px-4 py-2 rounded-lg transition-colors ${loading
                          ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                          : 'bg-green-600 text-white hover:bg-green-700'
                          }`}
                      >
                        Open Namuna K
                      </button> */}
                      <br /> {/* Just to separate the buttons */}

                      <button
                        onClick={handleSendToSamanvayClick}
                        disabled={loading || sendingToSamanvay}
                        className={`flex items-center px-4 py-2 rounded-lg transition-colors ${buttonDisabled
                          ? 'bg-red-600 text-white hover:bg-red-700'
                          : loading || sendingToSamanvay
                            ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                            : 'bg-purple-600 text-white hover:bg-purple-700'
                          }`}
                      >
                        {sendingToSamanvay ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            {translations[language]?.Sending}
                          </>
                        ) : loading ? (
                          <span>{translations[language]?.Checking}</span>
                        ) : (
                          <span>{translations[language]?.SendToSamanvayOfficer}</span>
                        )}
                      </button>
                    </>

                  )}

                  {/* Samanvay Button */}
                  {userRole === ROLES.SAMANVAY && (
                    <button
                      onClick={() => {
                        if (buttonDisabled) {
                          showConfirm(
                            'Confirm Re-submission',
                            'You have already sent once. Do you want to send again?',
                            () => {
                              setSendToRole('Collector');
                              setShowSendToCollectorModal(true);
                              fetchUsersCollector();
                            }
                          );
                          return;
                        }
                        setSendToRole('Collector');
                        setShowSendToCollectorModal(true);
                        fetchUsersCollector();
                      }}
                      disabled={loading || sendingToCollector}
                      className={`flex items-center px-4 py-2 rounded-lg transition-colors ${buttonDisabled
                        ? 'bg-red-600 text-white hover:bg-red-700'
                        : loading || sendingToCollector
                          ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                          : 'bg-purple-600 text-white hover:bg-purple-700'
                        }`}
                    >
                      {sendingToCollector ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {translations[language]?.Sending}
                        </>
                      ) : loading ? (
                        <span>{translations[language]?.Checking}</span>
                      ) : (
                        <span>{translations[language]?.SendToCollector}</span>
                      )}
                    </button>
                  )}

                  {/* Collector Button */}
                  {userRole === ROLES.COLLECTOR && (
                    <button
                      onClick={handleSendToLAOClick}
                      disabled={loading || sendingToLAO}
                      className={`flex items-center px-4 py-2 rounded-lg transition-colors ${loading || sendingToLAO
                        ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                        : buttonDisabled
                          ? 'bg-red-600 text-white hover:bg-red-700'
                          : 'bg-purple-600 text-white hover:bg-purple-700'
                        }`}

                    >
                      {sendingToLAO ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {translations[language]?.Assigning}
                        </>
                      ) : loading ? (
                        <span>{translations[language]?.Checking}</span>
                      ) : (
                        <span>{translations[language]?.AssignLAOOfficer}</span>
                      )}
                    </button>
                  )}

                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filter Panel - Appears below the action bar */}
        {showFilterPanel && !loadingRecords && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6 border border-gray-200">
            <div className="space-y-6">

              {/* Filter Mode Selection */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-1 bg-gray-100 rounded-xl p-1.5 shadow-inner">
                  <button
                    onClick={() => setFilterMode('location')}
                    className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-2 ${filterMode === 'location'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                      }`}
                  >
                    <MapPin className="h-4 w-4" />
                    <span>{translations[language]?.FilterByLocation}</span>
                  </button>
                  <button
                    onClick={() => setFilterMode('all')}
                    className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-2 ${filterMode === 'all'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                      }`}
                  >
                    <Globe className="h-4 w-4" />
                    <span>{translations[language]?.AllRecords}</span>
                  </button>
                </div>
              </div>

              {/* Location Selection - Only show when location mode is selected */}
              {filterMode === 'location' && (
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                  <div className="space-y-4">

                    {/* Header */}
                    <div className="flex items-center">
                      <MapPin className="h-5 w-5 text-blue-600 mr-2" />
                      <h3 className="text-lg font-semibold text-gray-800">{translations[language]?.SelectLocation}</h3>
                    </div>

                    {/* Location Dropdowns */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

                      {/* District Dropdown */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {translations[language]?.district} <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={selectedDistrict}
                          onChange={handleDistrictChange}
                          disabled={loadingDistricts}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed transition-all duration-200 shadow-sm"
                        >
                          <option value="">
                            {loadingDistricts ? <span>{translations[language]?.LoadingDistricts}</span> : <span>{translations[language]?.SelectDistrict}</span>}
                          </option>
                          {districts.map((district) => (
                            <option key={district.dist_name} value={district.dist_name}>
                              {district.dist_name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Taluka Dropdown */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {translations[language]?.taluka} <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={selectedTaluka}
                          onChange={handleTalukaChange}
                          disabled={!selectedDistrict || loadingTalukas}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed transition-all duration-200 shadow-sm"
                        >
                          <option value="">
                            {loadingTalukas ? <span>{translations[language]?.LoadingTalukas}</span> : <span>{translations[language]?.SelectTaluka}</span>}
                          </option>
                          {talukas.map((taluka) => (
                            <option key={taluka.taluka_name} value={taluka.taluka_name}>
                              {taluka.taluka_name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Village Dropdown */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {translations[language]?.village} <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={selectedVillage}
                          onChange={handleVillageChange}
                          disabled={!selectedTaluka || loadingVillages}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed transition-all duration-200 shadow-sm"
                        >
                          <option value="">
                            {loadingVillages ? <span>{translations[language]?.LoadingVillages}</span> : <span>{translations[language]?.SelectVillage}</span>}
                          </option>
                          {villages.map((village) => {
                            const villageId = village.village_name;
                            const villageName = village.village_name;
                            return (
                              <option key={villageId} value={villageId}>
                                {villageName}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    </div>

                    {/* Status Messages */}
                    {filterMode === 'location' && !hasValidLocationSelection && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <p className="text-sm text-amber-700">
                          {translations[language]?.PleaseSelectAll}
                        </p>
                      </div>
                    )}

                    {filterMode === 'location' && hasValidLocationSelection && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <p className="text-sm text-green-700 flex items-center">
                          <span className="mr-2">✓</span>
                          {translations[language]?.ShowingDataFor} {getCurrentLocationDescription()}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Data Tables - Show when records exist */}
        {canShowData && filteredRecords.length > 0 && !loadingRecords && (
          <div>

            {/* Records Table */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
              {(() => {
                const numberOfGroupsToShow = 4;
                const itemsPerGroup = Math.ceil(filteredRecords.length / numberOfGroupsToShow);
                const groups = [];

                for (let i = 0; i < numberOfGroupsToShow; i++) {
                  const groupStartIndex = i * itemsPerGroup;
                  const groupEndIndex = Math.min(groupStartIndex + itemsPerGroup, filteredRecords.length);
                  if (groupStartIndex < filteredRecords.length) {
                    groups.push(filteredRecords.slice(groupStartIndex, groupEndIndex));
                  } else {
                    groups.push([]);
                  }
                }

                return groups.map((group, groupIndex) => (
                  <div key={groupIndex} className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100 hover:shadow-2xl transition-shadow duration-300">

                    {/* Group Header */}
                    <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-4">
                      <h3 className="text-white font-semibold text-center">{translations[language]?.Column} {groupIndex + 1}</h3>
                    </div>

                    {/* Group Table */}
                    <div className="overflow-x-auto">
                      <table className="min-w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-b">{translations[language]?.SrNo}</th>
                            <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-b">{translations[language]?.SurveyNo}</th>
                            <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-b">{translations[language]?.Area}</th>
                            {userRole === 18 && (
                              <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-b">{translations[language]?.Action}</th>
                            )}                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {group.map((record, index) => (
                            <tr key={record.id} className={`hover:bg-blue-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                              <td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{record.seqNo}</td>
                              <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-700 font-medium" title={`Survey Type: ${record.survey_type || 'N/A'}`}>
                                {record.gatNo}
                                {record.survey_type && (
                                  <div className="text-xs text-gray-500">({record.survey_type})</div>
                                )}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-700 font-medium" title={`Total Area: ${record.total_area || 'N/A'} ${record.unit_name || ''}`}>
                                {record.area}
                                {record.unit_name && (
                                  <div className="text-xs text-gray-500">{record.unit_name}</div>
                                )}
                              </td>
                              {userRole === 18 && (

                                <td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-center">
                                  <button
                                    onClick={() => handleEditLandholding(record)}
                                    className="p-2 rounded-full text-purple-600 hover:bg-purple-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors"
                                    aria-label="Edit"
                                    title={`Edit landholding for ${record.landholder_name || 'this land holding'}`}
                                  >
                                    <Edit2 className="h-4 w-4" />
                                  </button>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ));
              })()}
            </div>

            {/* Summary Stats */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white p-4 rounded-lg shadow">
                <div className="text-sm text-gray-600">{translations[language]?.TotalSurveyGat}</div>
                <div className="text-2xl font-bold text-blue-600">{filteredRecords.length}</div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <div className="text-sm text-gray-600">{translations[language]?.TotalArea}</div>
                <div className="text-2xl font-bold text-green-600">
                  {filteredRecords.reduce((sum, record) => sum + (parseFloat(record.total_area) || 0), 0).toFixed(2)}
                </div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <div className="text-sm text-gray-600">{translations[language]?.IrrigativeArea}</div>
                <div className="text-2xl font-bold text-orange-600">
                  {filteredRecords.reduce((sum, record) => sum + (parseFloat(record.irrigable_area) || 0), 0).toFixed(2)}
                </div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <div className="text-sm text-gray-600">{translations[language]?.AcquisitionArea}</div>
                <div className="text-2xl font-bold text-purple-600">
                  {filteredRecords.reduce((sum, record) => sum + (parseFloat(record.area) || 0), 0).toFixed(2)}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* No Data Message */}
        {canShowData && filteredRecords.length === 0 && !loadingRecords && (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <div className="text-gray-400 mb-4">
              <FileText className="h-16 w-16 mx-auto" />
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">{translations[language]?.NoRecordsFound}</h3>
            {(() => {
              const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || '{}');
              return selectedProject?.project_name ? (
                <div>
                  <p className="text-gray-600 mb-2">
                    {translations[language]?.NoLandRecordsForProject}
                  </p>
                  <p className="text-blue-600 font-medium">
                    {selectedProject.project_name} ({selectedProject.project_type})
                  </p>
                  <p className="text-gray-500 text-sm mt-2">
                    {translations[language]?.TryAnother}
                  </p>
                </div>
              ) : (
                <p className="text-gray-600">{translations[language]?.NoLandRecords}</p>
              );
            })()}
          </div>
        )}

        {/* Location Selection Required Message */}
        {filterMode === 'location' && !hasValidLocationSelection && !loadingRecords && (
          <div className="mt-6 text-center py-12">
            <div className="max-w-md mx-auto">
              <MapPin className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg font-medium mb-2">{translations[language]?.LocationSelectionRequired}</p>
              <p className="text-gray-400 text-sm">{translations[language]?.PleaseSelectFromFilter}</p>
            </div>
          </div>
        )}

      </div>

      {/* =============================================================================
          MODALS - All modals organized together
          ============================================================================= */}

      {/* Download Modal */}
      {/* <DownloadModal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        data={filteredRecords}
        selectedLocation={getLocationNames()}
      /> */}

      {/* Send to Collector/LAO/Samanvay Modal */}
      {showSendToCollectorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">

              {/* Modal Header */}
              {/* <div className="flex justify-between items-center mb-6"> */}
              {/* <div className="text-right mb-6">
                <h3 className="text-xl font-semibold text-gray-800">
                  {sendToRole === 'Samanvay' ? 'Send to Samanvay Officer' :
                    sendToRole === 'Collector' ? 'Send to Collector' :
                      'Assign LAO Officer'}
                </h3>
                <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                  <X className="h-6 w-6" />
                </button>
              </div> */}
              {/* Show restoration message only when restored */}
              {shouldRestoreModal && modalParams && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-700">
                    Previous selections restored.
                    {modalParams.selectedDocumentName &&
                      ` Re-select document: ${modalParams.selectedDocumentName}`
                    }
                  </p>
                </div>
              )}
              {/* Modal Content */}
              <div className="space-y-4">

                {/* User Selection based on role */}
                <div className="space-y-6">

                  {/* Collector Role - Show LAO selection */}
                  {sendToRole === 'Collector' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {translations[language]?.ProposeLAOOfficer} *
                      </label>
                      <select
                        value={selectedLao}
                        onChange={(e) => {
                          setSelectedLao(e.target.value);
                          setChangeLao(true);
                        }}
                        disabled={loadingUsers}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">
                          {loadingUsers ? <span>{translations[language]?.LoadingLAOUsers}</span> : <span>{translations[language]?.SelectLAOOfficer}</span>}
                        </option>

                        {/* Pre-assigned LAOs */}
                        {assignLAO && Array.isArray(assignLAO) && assignLAO.map((user) => (
                          <option key={`assigned-${user.usr_id || user.id}`} value={user.usr_id || user.id}>
                            {user.full_name || user.user_name} ({user.usr_role}) - {translations[language]?.Assigned}
                          </option>
                        ))}

                        {/* All other LAOs */}
                        {laoUsers && Array.isArray(laoUsers) && laoUsers
                          .filter(lao => !assignLAO || !Array.isArray(assignLAO) || !assignLAO.some(assigned =>
                            (assigned.usr_id || assigned.id) === (lao.usr_id || lao.id)
                          ))
                          .map((user) => (
                            <option key={`all-${user.usr_id || user.id}`} value={user.usr_id || user.id}>
                              {user.full_name || user.user_name} ({user.usr_role})
                            </option>
                          ))}
                      </select>

                      {/* Hidden Collector Selection */}
                      <div className="hidden">
                        <select
                          value={selectedUser}
                          onChange={(e) => setSelectedUser(e.target.value)}
                          disabled={loadingUsers}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">{loadingUsers ? <span>{translations[language]?.LoadingUsers}</span> : <span>{translations[language]?.SelectCollector}</span>}</option>
                          {users.map((user) => (
                            <option key={user.id || user.usr_id} value={user.id || user.usr_id}>
                              {user.full_name || user.user_name} ({user.usr_role})
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {/* LAO Role */}
                  {sendToRole === 'LAO' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {translations[language]?.SelectLAOOfficer} *
                      </label>
                      <select
                        value={selectedLao}
                        onChange={(e) => {
                          setSelectedLao(e.target.value);
                          setChangeLao(true);
                        }}
                        disabled={loadingUsers}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">
                          {loadingUsers ? <span>{translations[language]?.LoadingLAOUsers}</span> : <span>{translations[language]?.SelectLAOOfficer}</span>}
                        </option>

                        {/* Pre-assigned LAOs */}
                        {assignLAO && Array.isArray(assignLAO) && assignLAO.map((user) => (
                          <option key={`assigned-${user.usr_id || user.id}`} value={user.usr_id || user.id}>
                            {user.full_name || user.user_name} ({user.usr_role}) - Assigned
                          </option>
                        ))}

                        {/* All other LAOs */}
                        {laoUsers && Array.isArray(laoUsers) && laoUsers
                          .filter(lao => !assignLAO || !Array.isArray(assignLAO) || !assignLAO.some(assigned =>
                            (assigned.usr_id || assigned.id) === (lao.usr_id || lao.id)
                          ))
                          .map((user) => (
                            <option key={`all-${user.usr_id || user.id}`} value={user.usr_id || user.id}>
                              {user.full_name || user.user_name} ({user.usr_role})
                            </option>
                          ))}
                      </select>
                    </div>
                  )}

                  {/* Samanvay Role */}
                  {sendToRole === 'Samanvay' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {translations[language]?.SelectSamanvayOfficer} *
                      </label>
                      <select
                        value={selectedUser}
                        onChange={(e) => setSelectedUser(e.target.value)}
                        disabled={loadingUsers}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">
                          {loadingUsers ? <span>{translations[language]?.LoadingUsers}</span> : <span>{translations[language]?.SelectSamanvayOfficer}</span>}
                        </option>
                        {users && Array.isArray(users) && users.map((user) => (
                          <option key={user.id || user.usr_id} value={user.id || user.usr_id}>
                            {user.full_name || user.user_name} ({user.usr_role})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {/* Remarks */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {translations[language]?.Remarks}
                  </label>
                  <textarea
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    rows={3}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={translations[language]?.EnterRemarks}
                  />
                </div>

                {/* Document Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {translations[language]?.UploadDocument} *
                  </label>
                  <input
                    type="file"
                    onChange={(e) => setSelectedDocument(e.target.files[0])}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  />
                  {selectedDocument && (
                    <div className="mt-2 p-3 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm text-green-700 font-medium truncate max-w-[60%]">
                          {translations[language]?.Selected}: {selectedDocument.name}
                        </p>
                        <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">
                          {(selectedDocument.size / 1024 / 1024).toFixed(2)} MB
                        </span>
                      </div>
                      <div className="flex gap-2">
                        {selectedDocument.type === 'application/pdf' && (
                          <>
                            <button
                              type="button"
                              onClick={() => {
                                if (selectedDocument?.type === 'application/pdf') {
                                  setPdfForSigning(selectedDocument);
                                  setShowSignaturePad(true);
                                }
                              }}
                              disabled={uploadStatus === 'uploading'}
                              className="px-3 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-1 transition-colors"
                            >
                              {uploadStatus === 'uploading' ? (
                                <>
                                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                  Signing...
                                </>
                              ) : (
                                <>
                                  <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                  </svg>
                                  Sign Doc
                                </>
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleViewDocument(
                                uploadStatus === 'success' ? selectedSignDocument : selectedDocument
                              )}
                              className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-1 transition-colors"
                            >
                              <Eye className="h-3 w-3" />
                              {translations[language]?.View}
                            </button>
                          </>
                        )}
                      </div>
                      {/* Upload status messages */}
                      {uploadStatus === 'success' && (
                        <div className="mt-2 p-2 bg-green-100 border border-green-300 rounded text-xs text-green-700">
                          ✅ Document Signed Successfully
                        </div>
                      )}
                      {uploadStatus === 'error' && uploadError && (
                        <div className="mt-2 p-2 bg-red-100 border border-red-300 rounded text-xs text-red-700">
                          ❌ {uploadError}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {sendToRole === 'Collector' && (
                  <div className="flex justify-start">
                    <button
                      onClick={handleCoveringLetterClick}
                      className="px-4 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                    >
                      भूसंपादन अधिकारी नियुक्ती
                    </button>
                  </div>
                )}
              </div>

              {/* Modal Actions */}
              <div className="flex flex-col gap-4 mt-6 pt-4 border-t">

                {/* Buttons Row */}
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowSendToCollectorModal(false)}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                    disabled={sendingToCollecotr || sendingToSamanvay || sendingToLAO || showProgress}
                  >
                    {translations[language]?.Cancel}
                  </button>

                  <button
                    onClick={handleSendToSurveyor}
                    disabled={
                      showProgress ||
                      (sendToRole === 'Samanvay' && (!selectedUser || !selectedDocument || sendingToSamanvay)) ||
                      (sendToRole === 'Collector' && (!selectedUser || !selectedLao || !selectedDocument || sendingToCollector)) ||
                      (sendToRole === 'LAO' && (!selectedLao || !selectedDocument || sendingToLAO))
                    }
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
                  >
                    {(sendingToSamanvay && sendToRole === 'Samanvay') ||
                      (sendingToCollector && sendToRole === 'Collector') ||
                      (sendingToLAO && sendToRole === 'LAO') || showProgress ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {showProgress ? translations[language].saving : translations[language].CommonSave}
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 h-4 w-4" />
                        {translations[language].CommonSave}
                      </>
                    )}
                  </button>
                </div>

                {/* Progress Bar - Full width under buttons */}
                {showProgress && (
                  <div className="w-full">
                    <ProgressBar progress={uploadProgress} isVisible={showProgress} />
                  </div>
                )}
              </div>

            </div>
          </div>
          {/* SignaturePad Modal */}
          {showSignaturePad && pdfForSigning && (
            <SignaturePad
              pdfFile={pdfForSigning}
              onSign={(signaturesArray) => {
                setShowSignaturePad(false);
                signDocument(pdfForSigning, signaturesArray).then(() => {
                  setPdfForSigning(null);
                });
              }}
              onCancel={() => {
                setShowSignaturePad(false);
                setPdfForSigning(null);
              }}
            />
          )}
        </div>
      )}

      {/* Edit Landholding Modal */}
      {showEditLandholdingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <EditLandholdingList
              LandAcquisition_id={editingLandholdingId}
              onBack={async () => {
                setShowEditLandholdingModal(false);
                setEditingLandholdingId(null);
                await refreshRecords();
                setSuccessMessage('Landholding updated successfully!');
                setShowSuccess(true);
                setTimeout(() => setShowSuccess(false), 3000);
              }}
            />
          </div>
        </div>
      )}

      {/* Document Viewer Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-xl font-semibold text-gray-800">{translations[language]?.DocumentPreview}</h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  URL.revokeObjectURL(documentUrl);
                }}
                className="text-gray-400 hover:text-gray-700 text-2xl font-bold"
              >
                ×
              </button>
            </div>

            {/* Document Viewer */}
            <iframe
              src={documentUrl}
              className="w-full h-[80vh] rounded border border-gray-300"
              title="Document Preview"
            />
          </div>
        </div>
      )}

      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-sm mx-4">
            <div className="p-6">
              <div className={`w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center ${confirmConfig.type === 'danger' ? 'bg-red-100' :
                confirmConfig.type === 'warning' ? 'bg-yellow-100' :
                  'bg-blue-100'
                }`}>
                {confirmConfig.type === 'danger' ? '⚠' :
                  confirmConfig.type === 'warning' ? '⚠' : '?'}
              </div>

              <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">
                {confirmConfig.title}
              </h3>

              <p className="text-gray-600 text-sm mb-6 text-center">
                {confirmConfig.message}
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    if (confirmConfig.onCancel) confirmConfig.onCancel();
                  }}
                  className="flex-1 py-2 px-4 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                >
                  {translations[language]?.Cancel}
                </button>
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    if (confirmConfig.onConfirm) confirmConfig.onConfirm();
                  }}
                  className={`flex-1 py-2 px-4 text-white rounded-md transition-colors ${confirmConfig.type === 'danger' ? 'bg-red-600 hover:bg-red-700' :
                    confirmConfig.type === 'warning' ? 'bg-yellow-600 hover:bg-yellow-700' :
                      'bg-blue-600 hover:bg-blue-700'
                    }`}
                >
                  {translations[language]?.Confirm}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Simple Alert Notification */}
      {showAlertModal && (
        <div className="fixed top-4 right-4 z-50">
          <div className={`bg-white rounded-lg shadow-lg border-l-4 p-4 max-w-sm ${alertConfig.type === 'error' ? 'border-red-500' :
            alertConfig.type === 'warning' ? 'border-yellow-500' :
              alertConfig.type === 'info' ? 'border-blue-500' :
                'border-green-500'
            }`}>
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${alertConfig.type === 'error' ? 'bg-red-100' :
                  alertConfig.type === 'warning' ? 'bg-yellow-100' :
                    alertConfig.type === 'info' ? 'bg-blue-100' :
                      'bg-green-100'
                  }`}>
                  <span className={`font-bold ${alertConfig.type === 'error' ? 'text-red-600' :
                    alertConfig.type === 'warning' ? 'text-yellow-600' :
                      alertConfig.type === 'info' ? 'text-blue-600' :
                        'text-green-600'
                    }`}>
                    {alertConfig.type === 'error' ? '✕' :
                      alertConfig.type === 'warning' ? '⚠' :
                        alertConfig.type === 'info' ? 'ℹ' : '✓'}
                  </span>
                </div>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-gray-900">{alertConfig.title}</h3>
                <p className="text-sm text-gray-600 mt-1">{alertConfig.message}</p>
              </div>
              <button
                onClick={() => setShowAlertModal(false)}
                className="ml-4 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {showPDFModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white w-[70%] h-[90%] rounded-lg overflow-hidden shadow-2xl relative">
            <div className="flex justify-between items-center p-4 bg-blue-600 text-white">
              <h2 className="text-lg font-semibold">{translations[language]?.ScheduleOnePreview}</h2>
              <button onClick={() => setShowPDFModal(false)}>
                <X className="w-6 h-6" />
              </button>
            </div>

            <iframe
              id="pdfPreviewIframe"
              src={pdfBlobUrl}
              title="PDF Preview"
              className="w-full h-[calc(100%-60px)]"
            />


            <div className="absolute bottom-0 left-0 right-0 bg-gray-100 p-3 flex justify-end">
              <button
                onClick={() => {
                  const iframe = document.getElementById('pdfPreviewIframe');
                  if (iframe?.contentWindow) {
                    iframe.contentWindow.focus();
                    iframe.contentWindow.print();
                  }
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {translations[language]?.Download}
              </button>

            </div>
          </div>
        </div>
      )}

      {/* Namuna K Modal */}
      {showNamunaKModal && <NamunaK isOpen={showNamunaKModal} onClose={handleCloseNamunaK} data={[]} />}

    </div>
  );
}